import { usePwaUpdateStore } from '@/core/pwa/pwaUpdateStore'
import { useI18n } from '@runtime/core/i18n/i18n'
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import PlayBottomNavigation from './PlayBottomNavigation'
import PlayDesktopNavigation from './PlayDesktopNavigation'
import PlayHeader from './PlayHeader'
import PlayMobileMenu from './PlayMobileMenu'
import { PlayPresentationContext } from './PlayPresentation'
import { PlayMenuContext } from './playMenuContext'
import styles from './PlayShell.module.scss'

export default function PlayShell({
  gateway,
  children,
  className,
}: {
  gateway: boolean
  children: ReactNode
  className?: string
}) {
  const { t } = useI18n()
  const applyingUpdate = usePwaUpdateStore(state => state.applying)
  const { pathname } = useLocation()
  const [immersiveViews, setImmersiveViews] = useState<Set<symbol>>(() => new Set())
  const [menuOpen, setMenuOpen] = useState(false)
  const menuButton = useRef<HTMLButtonElement>(null)
  const menuOpener = useRef<HTMLElement | null>(null)
  const menuVisible = useRef(false)
  const beforeNavigate = useRef<(() => boolean | void) | undefined>(undefined)
  const returnToMenu = useRef<(() => boolean | void) | undefined>(undefined)
  const challenges = pathname === '/challenges' || pathname.startsWith('/challenges/')
  const standard = !gateway && immersiveViews.size === 0
  const registerImmersive = useCallback(() => {
    const token = Symbol()
    setImmersiveViews(current => new Set(current).add(token))
    return () =>
      setImmersiveViews(current => {
        const next = new Set(current)
        next.delete(token)
        return next
      })
  }, [])
  const presentation = useMemo(
    () => ({ standard, registerImmersive }),
    [standard, registerImmersive],
  )
  const closeMenu = useCallback(() => {
    if (!menuVisible.current) return
    menuVisible.current = false
    setMenuOpen(false)
    const target =
      menuOpener.current === menuButton.current && window.matchMedia('(min-width: 1024px)').matches
        ? menuButton.current?.closest('header')?.querySelector('a')
        : menuOpener.current
    target?.focus({ preventScroll: true })
  }, [])
  const openMenu = useCallback(
    (trigger: HTMLElement, leave?: () => boolean | void, returnToSetup?: () => boolean | void) => {
      menuOpener.current = trigger
      beforeNavigate.current = leave
      returnToMenu.current = returnToSetup
      menuVisible.current = true
      setMenuOpen(true)
    },
    [],
  )
  const menu = useMemo(
    () => ({
      open: menuOpen,
      openMenu,
      closeMenu,
      beforeNavigate: beforeNavigate.current,
      returnToMenu: returnToMenu.current,
    }),
    [menuOpen, openMenu, closeMenu],
  )
  useEffect(() => closeMenu(), [pathname, standard, closeMenu])

  return (
    <PlayPresentationContext.Provider value={presentation}>
      <PlayMenuContext.Provider value={menu}>
        <div
          className={[
            styles.shell,
            standard && pathname === '/home' ? styles.homePresentation : '',
            standard && challenges ? styles.challengesPresentation : '',
            className,
            standard ? styles.standard : '',
            gateway ? styles.gateway : 'app-shell',
          ]
            .filter(Boolean)
            .join(' ')}
          inert={applyingUpdate || undefined}
          data-testid="play-shell"
          data-presentation={gateway ? 'gateway' : standard ? 'standard' : 'immersive'}
        >
          {standard && (
            <a className={styles.skipLink} href="#play-content">
              {t('common.playNavigation.skipContent')}
            </a>
          )}
          {standard && (
            <PlayHeader
              onOpenMenu={() => {
                if (menuButton.current) openMenu(menuButton.current)
              }}
              menuOpen={menuOpen}
              menuButtonRef={menuButton}
            />
          )}
          {standard && <PlayDesktopNavigation />}
          <div
            id="play-content"
            tabIndex={-1}
            className={styles.content}
            data-testid="play-shell-content"
          >
            {children}
          </div>
          {standard && challenges && (
            <PlayBottomNavigation menuOpen={menuOpen} onOpenMenu={trigger => openMenu(trigger)} />
          )}
          <PlayMobileMenu open={!gateway && menuOpen} immersive={!standard} onClose={closeMenu} />
        </div>
      </PlayMenuContext.Provider>
    </PlayPresentationContext.Provider>
  )
}
